/**
 * RoundController — the Round (corner-fillet) transform-handle
 * machinery (issue #100) extracted from ConstructionTool (#837, epic
 * #828). A tool OWNS a controller instance and DRIVES it by
 * composition (it is NOT a base class), mirroring the
 * `RotationController` / `DeleteSliceController` pattern: the
 * controller owns the round drag STATE (`roundCornerDrag`), the
 * per-event Ctrl/Cmd modifier snapshot (`roundModifierHeldAtEvent`)
 * that drives `effectiveRoundAll`, and the round sub-mode's
 * peripheral preset / live-readout fields, while everything
 * construction-specific it needs from the owning tool (scope / scene
 * / project / commit / settings, the selected-line preference set,
 * and the derived-line guard) is injected through the `RoundHost`
 * interface.
 *
 * The heavy geometry lives in `src/model/constructionRounding.ts`
 * (`getCornerGeometry` / `enumerateRoundableCorners` /
 * `applyCornerRounding` / `radiusFromHandlePosition`); only the hover
 * / preview / commit state + handlers move here.
 *
 * ConstructionTool's `onDown` / `onMove` / `onUp` / `onCancel`
 * delegate the round case, preserving the exact original dispatch
 * order (including the pre-offset Ctrl/Cmd modifier capture on `onUp`
 * via `captureShiftModifier`).
 */
import paper from "paper";
import type { PointerSample } from "../canvas/PointerInput";
import type { GuideRay, Scene } from "../canvas/Scene";
import {
  isVisible,
  projectConstructionLines,
  type ConstructionLine,
  type Project,
} from "../model/Project";
import {
  applyCornerRounding,
  enumerateRoundableCorners,
  getCornerGeometry,
  handleDistanceFromCorner,
  radiusFromHandlePosition,
  type CornerGeometry,
} from "../model/constructionRounding";
import {
  hitCornerHandle,
  ROUND_MIN_RADIUS_MM,
  type RoundableOutline,
} from "../model/cornerHandles";
import {
  deriveConstructionOffsets,
  isOffsetDerived,
} from "../model/constructionOffset";
import type { ToolSettings } from "./EditorContext";
import { cssVar } from "../colors";

/** Dashed stroke colour for the live rounded-corner preview. */
const PREVIEW_STROKE = () => cssVar("--construction-preview");

/** Everything the RoundController needs from its owning tool. The
 *  round drag-state field lives in the controller; the shared
 *  hit-testing preferences (selected-line ids) and derived-line
 *  predicate stay in ConstructionTool and are injected here. */
export interface RoundHost {
  getScope(): paper.PaperScope;
  getScene(): Scene;
  getSettings(): ToolSettings;
  getProject(): Project;
  commit(label: string, mutate: (proj: Project) => Project): void;
  /** Ids of the currently-selected construction lines — a corner
   *  handle on a selected line wins ties (issue #262). */
  selectedConstructionLineIds(): Set<string>;
  /** True for any derived (offset OR mirror) line — round refuses
   *  them (their geometry is owned by a source line). */
  isDerivedLineId(id: string): boolean;
}

export class RoundController {
  /** Round sub-mode drag state (issue #100). Engaged on `onDown`
   *  when the user clicks the inside-the-corner handle of an
   *  existing construction line while the Round sub-mode is active.
   *  Per-frame `onMove` projects the cursor onto the corner's
   *  interior bisector to recover the implied radius and previews
   *  the resulting arc; `onUp` commits the rounding to the line's
   *  `cornerRounding` metadata (which the commit pipeline then
   *  applies to derive the new `pathData`). */
  private roundCornerDrag: {
    constructionId: string;
    pathIndex: number;
    segmentIndex: number;
    /** Full geometry of the targeted corner: anchor point, interior
     *  bisector direction (unit vector pointing toward the arc
     *  centre), half-angle, max permitted radius. Captured at down
     *  so per-frame `onMove` doesn't have to re-parse the path. */
    geom: CornerGeometry;
    /** Cursor offset from the handle dot at down — applied to every
     *  per-frame cursor so dragging from a click that grazed the
     *  edge of the handle doesn't snap the handle to under the
     *  cursor at the start of the drag. */
    grabOffset: { x: number; y: number };
    /** Radius the cursor's most recent position implies (clamped). */
    radius: number;
    /** Pointer position at down — used to detect click-without-drag
     *  gestures so a preset-radius click can apply without sweeping. */
    start: { x: number; y: number };
    /** Ephemeral preview path drawn at the proposed rounding. */
    previewItem: paper.Item | null;
  } | null = null;

  /** Round sub-mode preset radius (issue #100). When set, the next
   *  click on a sharp corner commits at this radius without
   *  requiring a drag — same opt-in pattern as the Offset distance
   *  preset. `null` falls back to drag-defined radius. */
  private presetCornerRadius: number | null = null;
  private liveCornerRadiusCallback?: (mm: number) => void;
  /** Snapshot of the Ctrl/Cmd modifier state captured at the last
   *  Round-mode pointer event. The drag uses this AND the
   *  `settings.roundAllCorners` flag to compute its "effective"
   *  mode. Captured per-event rather than read live so the keyboard
   *  listener that drives the snap-toolbar toggle's visual flip stays
   *  decoupled from the drag's per-frame math. */
  private roundModifierHeldAtEvent = false;

  constructor(private host: RoundHost) {}

  setPresetCornerRadius(mm: number | null): void {
    this.presetCornerRadius = mm;
  }

  setLiveCornerRadiusCallback(fn: ((mm: number) => void) | undefined): void {
    this.liveCornerRadiusCallback = fn;
  }

  /** True iff a round drag is currently in flight. */
  isDragActive(): boolean {
    return this.roundCornerDrag !== null;
  }

  /** Called from app.ts when the snap toolbar's effective round
   *  mode flips (toggle clicked OR Ctrl/Cmd state changed) while
   *  the Construction tool is in Round sub-mode. Refreshes the
   *  in-flight drag preview (if any) so the previewed geometry
   *  picks up the new effective mode without waiting for the next
   *  mouse-move. No-op when there's no drag in flight — the next
   *  drag will read the up-to-date mode at onDown anyway. */
  notifyRoundModeChanged(): void {
    if (!this.roundCornerDrag) return;
    // We don't get a fresh cursor sample here, so re-read the
    // cursor from the existing drag state by inverting the grab
    // offset that was captured at onDown. The actual cursor
    // position has likely moved since the last onMove, but using
    // the last-known-good projection keeps the preview consistent
    // — the next real onMove will refresh against the true cursor.
    const drag = this.roundCornerDrag;
    const cursor = {
      x:
        drag.geom.cornerPoint.x +
        drag.geom.bisectorDir.x *
          (drag.radius / Math.sin(drag.geom.halfAngle)) -
        drag.grabOffset.x,
      y:
        drag.geom.cornerPoint.y +
        drag.geom.bisectorDir.y *
          (drag.radius / Math.sin(drag.geom.halfAngle)) -
        drag.grabOffset.y,
    };
    this.refreshRoundPreview(cursor);
  }

  /** Effective "round all corners" mode at the time of the most
   *  recent Round pointer event — `settings.roundAllCorners OR
   *  shift-held`. Shift is a one-way modifier: while held it
   *  FORCES "all corners" regardless of the setting (matching the
   *  size-linking Shift behaviour elsewhere in the app); releasing
   *  it returns to whatever the toggle is set to. When the setting
   *  is already "all", Shift is a no-op. */
  private effectiveRoundAll(): boolean {
    return (
      this.host.getSettings().roundAllCorners || this.roundModifierHeldAtEvent
    );
  }

  // -------------------------------------------------------------------
  // Pointer-flow delegation (called by ConstructionTool)
  // -------------------------------------------------------------------

  /** onDown delegation for the Round sub-tool (#100): grab the
   *  inside-the-corner handle of a construction line and start a
   *  drag along the bisector to set the arc's radius. Derived
   *  (offset / mirror) lines refuse. */
  handleDown(
    pt: paper.Point,
    p: { x: number; y: number },
    s: PointerSample,
  ): boolean {
    this.roundModifierHeldAtEvent = !!s.shiftKey;
    // The handle visually sits at the centre of the arc that would
    // be inscribed at the current radius, so the drag reads as "pull
    // the arc centre away from the corner." Derived (offset) lines
    // refuse — their geometry is owned by a source line, so the user
    // should round the source instead.
    const hit = this.hitConstructionRoundCorner(pt);
    // Report whether the press engaged a corner, so the owning tool can give a
    // miss some other meaning (#1006: picking the line to work on) instead of
    // swallowing it.
    if (!hit) return false;
    if (this.host.isDerivedLineId(hit.constructionId)) return false;
    this.cancel();
    // Seed radius from the corner's committed entry (or 0 when
    // un-rounded). The preset doesn't seed during the drag — it
    // only enters the picture at commit, where a preset bigger
    // than `maxRadius` is treated as "doesn't fit, skip" rather
    // than clamped down (issue #100 follow-up).
    const seed = Math.min(hit.existingRadius ?? 0, hit.geom.maxRadius);
    this.roundCornerDrag = {
      constructionId: hit.constructionId,
      pathIndex: hit.pathIndex,
      segmentIndex: hit.segmentIndex,
      geom: hit.geom,
      grabOffset: {
        x: hit.handlePoint.x - p.x,
        y: hit.handlePoint.y - p.y,
      },
      radius: seed,
      start: { x: p.x, y: p.y },
      previewItem: null,
    };
    this.refreshRoundPreview(p);
    return true;
  }

  /** onMove delegation: recompute the radius the cursor implies and
   *  redraw the preview. Returns true when a round drag consumed
   *  the move. */
  handleMove(p: { x: number; y: number }, s: PointerSample): boolean {
    if (!this.roundCornerDrag) return false;
    this.roundModifierHeldAtEvent = !!s.shiftKey;
    // Per-frame: recompute the radius the cursor implies and redraw
    // the dashed preview path with the new arc inscribed.
    this.refreshRoundPreview(p);
    return true;
  }

  /** onUp: capture the Ctrl/Cmd modifier state for the effective
   *  round-all computation. Preserves the original dispatch order —
   *  this ran BEFORE the offset case in ConstructionTool.onUp (the
   *  two drags are mutually exclusive, so it only ever fires when a
   *  round drag is live). */
  captureShiftModifier(s: PointerSample): void {
    if (this.roundCornerDrag) {
      this.roundModifierHeldAtEvent = !!s.shiftKey;
    }
  }

  /** onUp delegation: commit (or un-round, or apply a preset on a
   *  click) the round drag, then clear its state + the Scene's
   *  transient overrides. Returns true when a round drag consumed
   *  the release. */
  handleUp(): boolean {
    if (!this.roundCornerDrag) return false;
    const drag = this.roundCornerDrag;
    if (drag.previewItem) drag.previewItem.remove();
    this.roundCornerDrag = null;
    // Clear the Scene's transient overrides so the next render
    // reads handle position / rounded-offset geometry from the
    // committed model rather than the in-flight drag state.
    // Without this, the transient radius lingers past the commit
    // and shadows the model — most visibly on undo, where the
    // committed `cornerRounding` reverts but the handle stays
    // parked at the dragged position (issue #244 follow-up).
    this.host.getScene().setTransientCornerHandle(null);
    this.host.getScene().setTransientRoundedOffsets(null);
    // Was this corner already rounded going in? If the user just
    // dragged the handle past the corner (negative projection
    // clamped to 0) we treat that as an explicit "un-round" — the
    // entry comes out of the metadata and the line returns to its
    // sharp source at that corner.
    const project = this.host.getProject();
    const line = projectConstructionLines(project).find(
      (l) => l.id === drag.constructionId,
    );
    const wasRounded = !!line?.cornerRounding?.corners.some(
      (c) =>
        c.pathIndex === drag.pathIndex && c.segmentIndex === drag.segmentIndex,
    );
    if (drag.radius < ROUND_MIN_RADIUS_MM) {
      if (wasRounded) {
        this.commitCornerRounding(
          drag.constructionId,
          drag.pathIndex,
          drag.segmentIndex,
          0,
        );
        return true;
      }
      // Stray click on an un-rounded corner — unless the user has
      // a preset locked, in which case a tap applies that preset.
      // A preset bigger than this corner's `maxRadius` is treated
      // as "doesn't fit" and skipped, rather than clamped down to
      // maxRadius — the user picked a specific dimension, we
      // don't silently substitute a different one (issue #100
      // follow-up).
      const preset = this.presetCornerRadius;
      if (
        preset !== null &&
        isFinite(preset) &&
        preset > 0 &&
        preset <= drag.geom.maxRadius
      ) {
        this.commitCornerRounding(
          drag.constructionId,
          drag.pathIndex,
          drag.segmentIndex,
          preset,
        );
      }
      return true;
    }
    this.commitCornerRounding(
      drag.constructionId,
      drag.pathIndex,
      drag.segmentIndex,
      drag.radius,
    );
    return true;
  }

  /** Drop any in-flight round-corner drag and remove its preview.
   *  Called from cancelDraft / onCancel / sub-mode switches. */
  cancel(): void {
    if (!this.roundCornerDrag) return;
    if (this.roundCornerDrag.previewItem) {
      this.roundCornerDrag.previewItem.remove();
    }
    this.roundCornerDrag = null;
    // Clear the Scene's transient handle override + offset previews
    // so the handle dot snaps back to its committed position and
    // any derived offset previews disappear.
    this.host.getScene().setTransientCornerHandle(null);
    this.host.getScene().setTransientRoundedOffsets(null);
    this.host.getScene().clearGuideRays();
  }

  // -------------------------------------------------------------------
  // Rays + preview
  // -------------------------------------------------------------------

  /** Radius rays for a round-corner drag (#746): a vector from each corner's arc
   *  centre out to its fillet curve (length = that corner's radius). In "all
   *  corners" mode every eligible corner of `sharp` is being rounded together,
   *  so emit a ray for each (its radius clamped to what the corner can fit);
   *  otherwise just the grabbed corner. */
  private buildCornerRadiusRays(
    sharp: string,
    scope: paper.PaperScope,
    radius: number,
    applyToAll: boolean,
    dragGeom: CornerGeometry,
  ): GuideRay[] {
    const rays: GuideRay[] = [];
    const addRay = (geom: CornerGeometry, r: number) => {
      const sinHalf = Math.sin(geom.halfAngle);
      if (r <= 0 || sinHalf <= 1e-3) return;
      const centreDist = r / sinHalf;
      const arcCentre = {
        x: geom.cornerPoint.x + geom.bisectorDir.x * centreDist,
        y: geom.cornerPoint.y + geom.bisectorDir.y * centreDist,
      };
      // Arc apex: back toward the corner from the centre by exactly `r`.
      const arcPoint = {
        x: arcCentre.x - geom.bisectorDir.x * r,
        y: arcCentre.y - geom.bisectorDir.y * r,
      };
      rays.push({ from: arcCentre, to: arcPoint, radiusMm: r });
    };
    if (applyToAll) {
      for (const c of enumerateRoundableCorners(
        sharp,
        scope,
        ROUND_MIN_RADIUS_MM,
      )) {
        const geom = getCornerGeometry(
          sharp,
          c.pathIndex,
          c.segmentIndex,
          scope,
        );
        if (geom) addRay(geom, Math.min(radius, geom.maxRadius));
      }
    } else {
      addRay(dragGeom, radius);
    }
    return rays;
  }

  /** Recompute the rounded-corner preview from the active drag's
   *  sharp source path and the cursor's projection onto the
   *  corner's interior bisector. The handle position is treated as
   *  the centre of the proposed arc — distance from the corner is
   *  `radius / sin(halfAngle)` — so the inverse gives the radius
   *  back from where the cursor is. Negative projections (the
   *  user dragged past the corner, away from the arc interior)
   *  clamp to 0, which the commit handler treats as "un-round
   *  this corner." A locked preset radius overrides the drag math
   *  entirely. */
  private refreshRoundPreview(cursor: { x: number; y: number }) {
    if (!this.roundCornerDrag) return;
    const drag = this.roundCornerDrag;
    const scope = this.host.getScope();
    const project = this.host.getProject();
    const line = projectConstructionLines(project).find(
      (l) => l.id === drag.constructionId,
    );
    if (!line) {
      this.cancel();
      return;
    }
    const sharp = line.cornerRounding?.sharpPathData ?? line.pathData;
    // Apply the grab offset so the handle "stays under" the cursor
    // from the exact spot the user clicked (preserves the small
    // delta between the click point and the handle's centre).
    const handlePos = {
      x: cursor.x + drag.grabOffset.x,
      y: cursor.y + drag.grabOffset.y,
    };
    let radius = radiusFromHandlePosition(drag.geom, handlePos);
    const preset = this.presetCornerRadius;
    if (preset !== null && isFinite(preset) && preset > 0) {
      // Preset overrides the cursor-defined radius — unless it
      // can't fit on this corner, in which case the preview shows
      // the line un-rounded (radius 0). A click-through-preset
      // commit at a too-big preset is also skipped by `onUp`, so
      // the visual matches what the commit would do.
      radius = preset <= drag.geom.maxRadius ? preset : 0;
    } else if (radius > drag.geom.maxRadius) {
      // Drag-defined radius still clamps to the corner's max — the
      // handle can't be visually pulled past that point either.
      radius = drag.geom.maxRadius;
    }
    drag.radius = radius;
    this.liveCornerRadiusCallback?.(radius);
    // #746: a radius ray from each manipulated corner's arc centre out to its
    // fillet curve. In "all corners" mode every eligible corner is being rounded
    // at once, so draw a ray on each; otherwise just the grabbed corner.
    const cornerRays = this.buildCornerRadiusRays(
      sharp,
      scope,
      radius,
      this.effectiveRoundAll(),
      drag.geom,
    );
    this.host.getScene().setGuideRays(cornerRays.length ? cornerRays : null);
    // Push the in-flight radius into the Scene so the corner-handle
    // dot follows the cursor along the bisector. In linked mode
    // (`applyToAll`), the Scene moves EVERY eligible handle on
    // this line — each along its OWN bisector — so the "all
    // corners are chained together" feedback reads visibly during
    // the drag, not just on commit. In single mode only the
    // grabbed corner's dot moves.
    this.host.getScene().setTransientCornerHandle({
      constructionId: drag.constructionId,
      pathIndex: drag.pathIndex,
      segmentIndex: drag.segmentIndex,
      radius,
      applyToAll: this.effectiveRoundAll(),
    });
    if (drag.previewItem) {
      drag.previewItem.remove();
      drag.previewItem = null;
    }
    // Build the full preview pathData. In single-corner mode the
    // existing per-corner radii are preserved and only the dragged
    // corner picks up the new radius (or loses it, when radius is
    // 0). In all-corners mode (default or Ctrl/Cmd-inverted) every
    // round-eligible corner on the line picks up the SAME new
    // radius — overwriting any prior per-corner radii — so a drag
    // produces a uniformly rounded shape (#100 follow-up).
    const applyToAll = this.effectiveRoundAll();
    let merged: Array<{
      pathIndex: number;
      segmentIndex: number;
      radius: number;
    }>;
    if (applyToAll) {
      const eligible = enumerateRoundableCorners(
        sharp,
        scope,
        ROUND_MIN_RADIUS_MM,
      );
      if (radius >= ROUND_MIN_RADIUS_MM) {
        merged = eligible.map((c) => ({
          pathIndex: c.pathIndex,
          segmentIndex: c.segmentIndex,
          radius,
        }));
      } else {
        merged = [];
      }
    } else {
      const existing = line.cornerRounding?.corners ?? [];
      merged = existing.filter(
        (c) =>
          c.pathIndex !== drag.pathIndex ||
          c.segmentIndex !== drag.segmentIndex,
      );
      if (radius >= ROUND_MIN_RADIUS_MM) {
        merged.push({
          pathIndex: drag.pathIndex,
          segmentIndex: drag.segmentIndex,
          radius,
        });
      }
    }
    const previewPath = applyCornerRounding(sharp, merged, scope) ?? sharp;
    try {
      const preview = new scope.CompoundPath({
        pathData: previewPath,
        insert: true,
      });
      preview.strokeColor = new scope.Color(PREVIEW_STROKE());
      preview.strokeWidth = 1.6;
      preview.strokeScaling = false;
      preview.dashArray = [4, 3];
      preview.fillColor = null;
      preview.data = { ephemeral: true };
      drag.previewItem = preview;
    } catch {
      /* ignore — preview only */
    }
    // Also recompute any offset lines that follow this source, so
    // their rendered geometry tracks the in-flight rounded outline
    // instead of waiting for the commit. Cheap path when no offsets
    // exist — `setTransientRoundedOffsets(null)` clears any previous
    // preview without doing the deriveConstructionOffsets work.
    const hasAnyOffsets = (project.constructionLines ?? []).some(
      (l) => l.offsetSource && l.offsetSource.sourceId === drag.constructionId,
    );
    if (!hasAnyOffsets) {
      this.host.getScene().setTransientRoundedOffsets(null);
      return;
    }
    const projectWithInFlight: Project = {
      ...project,
      constructionLines: (project.constructionLines ?? []).map((l) =>
        l.id === drag.constructionId ? { ...l, pathData: previewPath } : l,
      ),
    };
    const derived = deriveConstructionOffsets(projectWithInFlight, scope);
    const updates: Array<{ id: string; pathData: string }> = [];
    const originals = new Map(
      (project.constructionLines ?? []).map((l) => [l.id, l.pathData] as const),
    );
    for (const l of derived.constructionLines ?? []) {
      if (l.id === drag.constructionId) continue;
      const original = originals.get(l.id);
      if (original !== undefined && original !== l.pathData) {
        updates.push({ id: l.id, pathData: l.pathData });
      }
    }
    this.host
      .getScene()
      .setTransientRoundedOffsets(updates.length > 0 ? updates : null);
  }

  // -------------------------------------------------------------------
  // Hit-test + commit
  // -------------------------------------------------------------------

  /** Find the nearest round-corner handle within the standard pickup zone.
   *  The search itself is the shared `hitCornerHandle` (`cornerHandles.ts`,
   *  shared with the leather Round sub-tool #974) — it owns where a handle
   *  sits for a given radius and which one a click lands on. This method only
   *  decides WHICH lines are eligible and marks the selected ones preferred,
   *  so a handle on a selected line wins ties (#262).
   *
   *  Reads from `sharpPathData` (when present) so corners stay addressable at
   *  their original positions even after they've been rounded. */
  private hitConstructionRoundCorner(pt: paper.Point): {
    constructionId: string;
    pathIndex: number;
    segmentIndex: number;
    geom: CornerGeometry;
    handlePoint: paper.Point;
    /** Currently-stored radius for this corner, or undefined when
     *  the corner hasn't been rounded yet. The caller seeds the
     *  drag from this so an immediate-release click commits the
     *  same radius the user could see. */
    existingRadius: number | undefined;
  } | null {
    const scope = this.host.getScope();
    const preferIds = this.host.selectedConstructionLineIds();
    const outlines: RoundableOutline[] = [];
    for (const line of projectConstructionLines(this.host.getProject())) {
      if (!isVisible(line)) continue;
      if (isOffsetDerived(line)) continue;
      outlines.push({
        key: line.id,
        sharpPathData: line.cornerRounding?.sharpPathData ?? line.pathData,
        corners: line.cornerRounding?.corners ?? [],
        preferred: preferIds.has(line.id),
      });
    }
    const hit = hitCornerHandle(outlines, pt, scope, {
      presetRadiusMm: this.presetCornerRadius,
    });
    if (!hit) return null;
    return {
      constructionId: hit.key,
      pathIndex: hit.pathIndex,
      segmentIndex: hit.segmentIndex,
      geom: hit.geom,
      handlePoint: new scope.Point(hit.handlePoint.x, hit.handlePoint.y),
      existingRadius: hit.existingRadius,
    };
  }

  /** Commit a corner rounding to the line's `cornerRounding`
   *  metadata. Adds a new entry, or replaces the existing one for
   *  the same `(pathIndex, segmentIndex)`. The first rounding on a
   *  line also snapshots the current `pathData` as `sharpPathData`
   *  so subsequent re-derivations have a stable source.
   *  `deriveRoundedCorners` in the commit pipeline turns the
   *  updated metadata into the new visible `pathData`.
   *
   *  Passing `radius = 0` (or any value below the slop threshold)
   *  REMOVES the entry for this corner — used by the drag's
   *  "pulled the handle past the corner" branch to un-round a
   *  corner the user previously rounded. If that drop empties the
   *  corners array entirely, the whole `cornerRounding` block is
   *  cleared so the line returns to a pristine un-rounded state. */
  private commitCornerRounding(
    constructionId: string,
    pathIndex: number,
    segmentIndex: number,
    radius: number,
  ) {
    const applyToAll = this.effectiveRoundAll();
    const scope = this.host.getScope();
    const baseLabel =
      radius < ROUND_MIN_RADIUS_MM
        ? "Un-round construction-line corner"
        : "Round construction-line corner";
    const label = applyToAll
      ? baseLabel.replace("corner", "corners")
      : baseLabel;
    this.host.commit(label, (proj) => {
      const lines = proj.constructionLines ?? [];
      const idx = lines.findIndex((l) => l.id === constructionId);
      if (idx < 0) return proj;
      const line = lines[idx]!;
      const sharp = line.cornerRounding?.sharpPathData ?? line.pathData;
      const existing = line.cornerRounding?.corners ?? [];
      let filtered: Array<{
        pathIndex: number;
        segmentIndex: number;
        radius: number;
      }>;
      if (applyToAll) {
        // All-corners commit: drop every prior per-corner entry on
        // this line (they all get overwritten) and stamp the new
        // radius on every round-eligible corner of the sharp source.
        // Stamping radius 0 collapses to an empty list, which the
        // cleanup below turns into a metadata drop — un-rounds the
        // entire line in one shot.
        if (radius >= ROUND_MIN_RADIUS_MM) {
          filtered = enumerateRoundableCorners(
            sharp,
            scope,
            ROUND_MIN_RADIUS_MM,
          ).map((c) => ({
            pathIndex: c.pathIndex,
            segmentIndex: c.segmentIndex,
            radius,
          }));
        } else if (existing.length === 0) {
          // Nothing was rounded, nothing to un-round — skip the
          // commit so undo history stays tidy.
          return proj;
        } else {
          filtered = [];
        }
      } else {
        filtered = existing.filter(
          (c) => c.pathIndex !== pathIndex || c.segmentIndex !== segmentIndex,
        );
        if (radius >= ROUND_MIN_RADIUS_MM) {
          filtered.push({ pathIndex, segmentIndex, radius });
        } else if (filtered.length === existing.length) {
          // Nothing to remove — the user un-rounded a corner that
          // wasn't rounded. Skip the commit so undo history stays
          // tidy.
          return proj;
        }
      }
      // When the last entry drops, the line returns to its sharp
      // source — restore `pathData` from the saved sharp data and
      // clear the metadata. Without this, the rounded `pathData`
      // would linger after the metadata is gone: the next render
      // would no longer see a corner (the rounded anchors carry
      // bezier handles) and the round-corner knobs would silently
      // disappear. After the restore, the corners are sharp again
      // and the knobs come back at their rest positions, ready for
      // another rounding.
      const nextLine: ConstructionLine =
        filtered.length === 0
          ? { ...line, pathData: sharp, cornerRounding: undefined }
          : {
              ...line,
              cornerRounding: { sharpPathData: sharp, corners: filtered },
            };
      const nextLines = lines.slice();
      nextLines[idx] = nextLine;
      return { ...proj, constructionLines: nextLines };
    });
  }
}
